/* eslint-disable arrow-body-style */

import React, { useMemo } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import RangePicker from '@home/components/RangePicker';
import ReportDatePickerButton from './Button';
import { componentDefault } from '@intelligence/components/Filter/FilterBase/FilterList';
import { formatDateStr } from '@gp/utils/common';

import type { IBaseProps } from '@home/types/common';
import type { IDynamicFormControlValueAccessor } from '@gp/components/DynamicForm/components/FormControl/types';

import './index.scss';

export interface IReportDateValue {
  start_date: string;
  end_date: string;
}

interface IReportDatePickerProps extends IBaseProps, IDynamicFormControlValueAccessor<IReportDateValue> {
  disabledWeeks?: string[][];
}

const LANGUAGE = 'en';
const DATE_TYPE = 'week';
const DATE_TYPES = [DATE_TYPE];
const AUTO_RANGE = {
  timeType: DATE_TYPE,
  length: 0,
};

const ReportDatePicker: React.FC<IReportDatePickerProps> = (props) => {
  const { style, className, disabledWeeks, value, onChange } = props;

  // 符合RangePicker组件的禁用日期数据
  const disabledDate = useMemo<Record<string, (m: moment.Moment) => boolean>>(() => {
    return {
      [DATE_TYPE]: (date: moment.Moment) => {
        if (!moment.isMoment(date) || !date.isValid()) return false;
        if (!Array.isArray(disabledWeeks) || !disabledWeeks.length) return true;
        return !disabledWeeks.some((week) => {
          const [start, end] = week || [];
          return date.isBetween(start, end, 'day', '[]');
        });
      },
    };
  }, [disabledWeeks]);

  // 符合RangePicker组件的value
  const date = useMemo<[moment.Moment, moment.Moment] | ['', '']>(() => {
    const { start_date: startDate, end_date: endDate } = value || {};
    if (!startDate || !endDate) return ['', ''];
    return [moment(startDate), moment(endDate)];
  }, [value]);

  // RangePicker组件的change回调
  const handleDateChange = (value: any, type: string) => {
    let dateValue = ['', ''];

    if (Array.isArray(value) && value.length === 2) {
      const startDate = value[0];
      let endDate = value[1];
      if (startDate === '' && endDate === '') {
        dateValue = componentDefault?.[type];
      } else {
        if (type === 'year') {
          endDate = moment(endDate).endOf('year');
        }
        if (type === 'month') {
          endDate = moment(endDate).endOf('month');
        }
        dateValue = [startDate, endDate];
      }
    }

    onChange?.({
      start_date: formatDateStr(dateValue?.[0]),
      end_date: formatDateStr(dateValue?.[1]),
    });
  };

  return (
    <div style={style} className={classnames('search-item search-item-date dbg-dps-report-datepicker', className)}>
      <RangePicker
        className="search-border"
        language={LANGUAGE}
        showSelectType={false}
        showShortcut={false}
        syncInput={false}
        autoRange={AUTO_RANGE}
        startOfWeek={1}
        type={DATE_TYPE}
        typeList={DATE_TYPES}
        disabledDate={disabledDate}
        label={<ReportDatePickerButton dateType={DATE_TYPE} value={value} />}
        defaultValue={componentDefault}
        value={date}
        onChange={handleDateChange}
      />
    </div>
  );
};

export default ReportDatePicker;
